// Portuguese copy for /face-data-changes-2026 (hand-written from copy.en.ts; same shape, same facts). Brazilian
// Portuguese, like the pricing pages' copy.pt.ts.
import type { FaceDataCopy } from "./copy.en";

const pt: FaceDataCopy = {
  seo: {
    title: "Dados faciais a partir de 1 de novembro | Photohawk",
    description:
      "A partir de 1 de novembro de 2026, os dados faciais da busca por selfie são excluídos automaticamente 12 meses depois do envio de cada foto. Fotos, vendas, downloads e busca por número de peito não mudam. Veja como funciona e por quê.",
  },
  hero: {
    badge: "A partir de 1 de novembro de 2026",
    title: "Busca por selfie e dados faciais:",
    titleHighlight: "o que muda",
    subtitle:
      "A partir de 1 de novembro, os dados faciais que fazem a busca por selfie funcionar são excluídos automaticamente 12 meses depois do envio de cada foto. Suas fotos continuam exatamente onde estão. As leis de privacidade exigem um limite fixo como este, então não é algo que possamos deixar de fazer.",
    ctaPolicy: "Ler a política de dados faciais",
    ctaHow: "Ver como funciona",
  },
  timeline: {
    title: "Como funciona",
    intro: "Cada foto segue o mesmo caminho. O prazo é por foto: começa quando aquela foto é enviada.",
    label: "Linha do tempo animada: uma foto é enviada, a busca por selfie a encontra durante 12 meses, depois seus dados faciais são excluídos e a foto continua disponível.",
    caption:
      "Uma foto é enviada, a busca por selfie a encontra durante 12 meses e depois seus dados faciais são excluídos automaticamente. A foto, suas vendas e a busca por número de peito continuam iguais.",
    steps: [
      { id: "upload", when: "Dia 1", title: "Uma foto é enviada", body: "Os rostos nela viram dados faciais, para que os compradores a encontrem com uma selfie." },
      { id: "search", when: "Durante 12 meses", title: "A busca por selfie a encontra", body: "Os compradores tiram uma selfie e veem todas as fotos em que aparecem, como hoje." },
      { id: "delete", when: "Aos 12 meses", title: "Seus dados faciais são excluídos", body: "De forma automática e permanente. A foto nunca mais é indexada para a busca facial." },
      { id: "carry", when: "Depois", title: "A foto continua", body: "Os compradores ainda a encontram pelo número de peito, pelo horário ou navegando, e podem comprá-la e baixá-la." },
    ],
    scene: {
      selfie: "Selfie",
      upload: "Enviada",
      search: "Busca por selfie",
      months: "12 meses",
      deleted: "Dados excluídos",
      carry: "A foto fica",
      match: "Encontrada",
    },
  },
  dissolve: {
    title: "Dados faciais não são a foto",
    intro:
      "Dados faciais são um conjunto de números que descrevem os rostos de uma foto: um modelo facial. Eles servem só para comparar a selfie de um comprador. Excluí-los apaga o modelo e nada mais.",
    label: "Ilustração animada: uma foto com seu modelo facial ao lado; o modelo se desfaz enquanto a foto, seu número de peito e sua venda continuam.",
    caption: "O modelo facial se desfaz. A foto, seu número de peito e sua venda não mudam.",
    scene: { photo: "Foto", template: "Modelo facial", bib: "Peito 1234", sale: "Vendida", gone: "Excluído" },
  },
  november: {
    title: "O que acontece em 1 de novembro",
    intro: "As fotos enviadas há mais de 12 meses já passaram do limite, então são tratadas primeiro.",
    points: [
      {
        id: "backlog",
        title: "As fotos mais antigas perdem seus dados faciais primeiro",
        body: "Os dados faciais das fotos enviadas há mais de 12 meses são excluídos a partir de 1 de novembro. Fazemos isso em lotes, então pode levar alguns dias até chegar a todas as galerias. A partir de 1 de novembro, nenhum dado facial novo é criado para essas fotos.",
      },
      {
        id: "daily",
        title: "Depois, uma verificação diária",
        body: "A partir daí, um processo automático roda todos os dias e exclui os dados faciais de cada foto quando ela completa 12 meses. Não há nada para configurar nem para lembrar.",
      },
      {
        id: "ended",
        title: "Quando uma galeria inteira passa de 12 meses",
        body: "A busca por selfie termina nessa galeria. Os compradores que tentarem usá-la veem uma nota curta que indica a busca por número de peito e a navegação.",
      },
      {
        id: "off",
        title: "Galerias fechadas ou com a busca por selfie desativada",
        body: "Os dados faciais são excluídos 30 dias depois que uma galeria é fechada ou que sua busca por selfie é desativada. Nas galerias que já estavam fechadas ou desativadas antes de 1 de novembro, esses 30 dias começam em 1 de novembro.",
      },
    ],
  },
  unchanged: {
    title: "O que não muda",
    intro: "Excluir dados faciais nunca exclui uma foto. Todo o resto continua como hoje:",
    items: [
      { id: "photos", text: "Suas fotos e galerias" },
      { id: "sales", text: "Vendas, pedidos e seus ganhos" },
      { id: "downloads", text: "Downloads, inclusive nas galerias de download gratuito" },
      { id: "bib", text: "A busca por número de peito ou número, e por texto" },
      { id: "time", text: "A busca por horário e a navegação" },
      { id: "recent", text: "A busca por selfie nas fotos enviadas nos últimos 12 meses" },
    ],
  },
  buyers: {
    title: "O que seus compradores vão notar",
    intro: "A maioria procura suas fotos nos dias e semanas depois do evento, então a maioria não vai notar nada.",
    items: [
      { id: "older", text: "A busca por selfie não vai encontrar fotos enviadas há mais de 12 meses." },
      { id: "other", text: "Eles ainda podem encontrar essas fotos pelo número de peito, pelo horário ou navegando pela galeria, e comprá-las e baixá-las normalmente." },
      { id: "note", text: "Numa galeria em que todas as fotos têm mais de 12 meses, a busca por selfie mostra uma nota curta com as outras formas de buscar." },
      { id: "selfies", text: "As selfies usadas para buscar continuam nunca sendo armazenadas, como hoje." },
    ],
    tip: "Um bom hábito: lembre os compradores de buscar logo depois de cada evento, nos seus e-mails ou nas redes sociais.",
  },
  why: {
    title: "Por que isso está acontecendo",
    lead: "Não temos muito controle sobre isso. Vem das leis de privacidade, não de uma decisão de reduzir a busca por selfie.",
    paragraphs: [
      "Modelos faciais são dados biométricos, que as leis de privacidade tratam como alguns dos dados pessoais mais sensíveis que existem. Essas leis exigem que eles tenham um limite de retenção fixo e publicado, que não sejam guardados por mais tempo do que o necessário e que depois sejam excluídos automaticamente. Guardar dados faciais indefinidamente não é uma opção para ninguém que ofereça busca facial.",
      "As galerias da Photohawk são usadas no mundo todo, então uma única regra precisa atender a todas estas leis ao mesmo tempo:",
    ],
    laws: [
      { id: "gdpr", name: "RGPD e UK GDPR", text: "Dados biométricos usados para identificar pessoas são uma categoria especial de dados pessoais, e o princípio da limitação da conservação exige que não sejam guardados por mais tempo do que o necessário." },
      { id: "bipa", name: "Illinois (BIPA)", text: "Exige um cronograma de retenção público e por escrito, e a destruição dos dados biométricos quando sua finalidade é cumprida, com prazos fixos." },
      { id: "cubi", name: "Texas (CUBI)", text: "Identificadores biométricos devem ser destruídos num prazo razoável e no máximo um ano depois que termina a finalidade da coleta." },
      { id: "co", name: "Colorado", text: "Dados biométricos devem ser excluídos quando a finalidade é cumprida, com prazos máximos fixos e uma revisão anual." },
      { id: "wa", name: "Washington", text: "Identificadores biométricos não podem ser guardados por mais tempo do que o razoavelmente necessário para prestar o serviço." },
    ],
    ourChoice:
      "A única coisa que podíamos escolher era a duração. Escolhemos 12 meses: tempo suficiente para cobrir o período em que os compradores procuram suas fotos, e dentro dos prazos que essas leis definem.",
    note: "Esta página explica como a Photohawk trata dados faciais. Não é aconselhamento jurídico.",
  },
  faq: {
    title: "Perguntas",
    items: [
      { id: "photos", q: "Alguma das minhas fotos será excluída?", a: "Não. Só os dados faciais da busca por selfie são excluídos. Fotos, galerias, vendas, pedidos e downloads não mudam." },
      { id: "action", q: "Preciso fazer alguma coisa?", a: "Não. Tudo acontece automaticamente. Se quiser, lembre os compradores de buscar logo depois de cada evento." },
      { id: "longer", q: "Posso manter os dados faciais por mais tempo ou tirar minhas galerias disso?", a: "Não. O limite de 12 meses vale para todas as galerias da Photohawk, no mundo todo, e não podemos abrir exceções: um limite fixo que vale para todos é exatamente o que essas leis pedem." },
      { id: "sooner", q: "Os dados faciais podem ser excluídos antes?", a: "Sim. Desative a busca por selfie de uma galeria, ou feche-a, e seus dados faciais são excluídos 30 dias depois. Se você reativá-la dentro desses 30 dias, essa exclusão é cancelada." },
      { id: "request", q: "E se alguém pedir a exclusão dos seus dados faciais?", a: "Qualquer pessoa pode pedir a qualquer momento, na própria galeria, pela busca por selfie, ou por e-mail para support@photohawk.com. Os dados faciais dela são excluídos em até 30 dias, e você também pode fazer isso na hora nas configurações de busca por selfie da galeria." },
      { id: "find", q: "Como os compradores vão encontrar fotos mais antigas?", a: "Pela busca por número de peito ou número, pela busca por horário ou navegando pela galeria, como hoje. Só a busca por selfie deixa de encontrá-las." },
      { id: "selfies", q: "As selfies dos compradores são armazenadas?", a: "Não. Uma selfie é usada só para aquela busca e depois descartada, como hoje." },
      { id: "plan", q: "Isso depende do meu plano?", a: "Não. É igual em todos os planos e em todas as galerias." },
    ],
  },
  cta: {
    title: "Todos os detalhes",
    body: "Nossa política de dados faciais explica exatamente quais dados faciais guardamos, por quanto tempo e como são excluídos. Dúvidas? Escreva para nós e uma pessoa de verdade vai responder.",
    policy: "Ler a política de dados faciais",
    contact: "Escrever para support@photohawk.com",
    allProducts: "Política de dados faciais do Photohawk Studio e do app Photohawk",
  },
  pauseLabel: "Pausar animação",
  playLabel: "Reproduzir animação",
};

export default pt;
